import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Divider,
} from "@mui/joy";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock } from "lucide-react";

import { Button, IconButton } from "../../shared";
import { useTask } from "../../hooks/query";
import { useProject } from "../../hooks";
import { NewEvent } from "./NewEvent";
import { MonthView } from "./MonthView";
import { TimeGridView, type CalendarEvent } from "./TimeGridView";
import {
    addDays,
    formatDate,
    getStatusColor,
    getStatusName,
    getViewTitle,
    getWeekDays,
} from "./utils";

type CalendarView = "day" | "week" | "month";

interface CalendarV2Props {
    onEventsChange?: () => void;
}

export default function CalendarV2(props: CalendarV2Props = {}) {
    const { onEventsChange } = props;
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showModal, setShowModal] = useState(false);
    const [showEventDetails, setShowEventDetails] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [currentView, setCurrentView] = useState<CalendarView>("week");
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const { project } = useProject();

    const { useListSubtasks } = useTask();
    const listSubtasksSvc = useListSubtasks();

    const toEvent = (subtask: any): CalendarEvent => {
        const executionDate = subtask.ExecutionDate
            ? new Date(subtask.ExecutionDate).toISOString().split("T")[0]
            : formatDate(new Date());

        const executionTime = subtask.ExecutionDate
            ? new Date(subtask.ExecutionDate).toTimeString().slice(0, 5)
            : undefined;

        return {
            id: subtask.Id,
            title: subtask.Name,
            date: executionDate,
            time: executionTime,
            allDay: !executionTime,
            color: subtask.Tags?.Color || "purple",
            description: subtask.Description,
            projectId: subtask.ProjectId,
            taskStatus: subtask.Status,
            tags: subtask.Tags || {},
        };
    };

    const fetchSubtasks = async () => {
        if (!project?.id) {
            setEvents([]);
            return;
        }

        try {
            const response = await listSubtasksSvc.mutateAsync({
                ProjectId: project.id,
            });
            setEvents((response || []).map(toEvent));
        } catch (error) {
            console.error("Error fetching subtasks:", error);
        }
    };

    useEffect(() => {
        void fetchSubtasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        setCurrentDate(date);
        setShowModal(true);
    };

    const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedEvent(event);
        setShowEventDetails(true);
    };

    const handleAddEvent = () => {
        void (async () => {
            await fetchSubtasks();
            onEventsChange?.();
        })();
        setShowModal(false);
    };

    const handlePrev = () => {
        if (currentView === "day") {
            setCurrentDate((d) => addDays(d, -1));
            return;
        }
        if (currentView === "week") {
            setCurrentDate((d) => addDays(d, -7));
            return;
        }
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNext = () => {
        if (currentView === "day") {
            setCurrentDate((d) => addDays(d, 1));
            return;
        }
        if (currentView === "week") {
            setCurrentDate((d) => addDays(d, 7));
            return;
        }
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    const weekDays = getWeekDays(currentDate);
    const dayDays = [currentDate];

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.surface",
                flex: 1,
                border: "1px solid",
                borderColor: "rgba(128, 128, 128, 0.2)",
                borderRadius: "4px",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.18)",
                    flexShrink: 0,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Typography
                        level="title-md"
                        sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}
                    >
                        {getViewTitle(currentDate, currentView)}
                    </Typography>

                    <Button
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        onClick={handleToday}
                        sx={{ borderRadius: "4px", boxShadow: "none" }}
                    >
                        Today
                    </Button>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            onClick={handlePrev}
                            sx={{ borderRadius: "4px", boxShadow: "none" }}
                        >
                            <ChevronLeft size={18} />
                        </IconButton>
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            onClick={handleNext}
                            sx={{ borderRadius: "4px", boxShadow: "none" }}
                        >
                            <ChevronRight size={18} />
                        </IconButton>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "inline-flex",
                        border: "1px solid",
                        borderColor: "rgba(128, 128, 128, 0.2)",
                        borderRadius: "4px",
                        overflow: "hidden",
                        bgcolor: "background.level1",
                        boxShadow: "none",
                    }}
                >
                    {(["day", "week", "month"] as CalendarView[]).map((view, index) => {
                        const active = currentView === view;
                        return (
                            <Box
                                key={view}
                                component="button"
                                type="button"
                                onClick={() => setCurrentView(view)}
                                sx={{
                                    appearance: "none",
                                    m: 0,
                                    border: "none",
                                    borderRight:
                                        index < 2
                                            ? "1px solid rgba(128, 128, 128, 0.2)"
                                            : "none",
                                    borderRadius: 0,
                                    minHeight: 32,
                                    px: 1.75,
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    fontFamily: "inherit",
                                    textTransform: "capitalize",
                                    lineHeight: 1,
                                    boxShadow: "none",
                                    outline: "none",
                                    bgcolor: active ? "primary.solidBg" : "transparent",
                                    color: active ? "primary.solidColor" : "text.primary",
                                    "&:hover": {
                                        bgcolor: active
                                            ? "primary.solidBg"
                                            : "rgba(128, 128, 128, 0.08)",
                                    },
                                    "&:focus, &:focus-visible, &:active": {
                                        outline: "none",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                {view}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {currentView === "month" && (
                <MonthView
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    events={events}
                    onDateClick={handleDateClick}
                    onEventClick={handleEventClick}
                />
            )}

            {currentView === "week" && (
                <TimeGridView
                    days={weekDays}
                    events={events}
                    onDateClick={handleDateClick}
                    onEventClick={handleEventClick}
                />
            )}

            {currentView === "day" && (
                <TimeGridView
                    days={dayDays}
                    events={events}
                    onDateClick={handleDateClick}
                    onEventClick={handleEventClick}
                />
            )}

            <NewEvent
                showModal={showModal}
                setShowModal={setShowModal}
                selectedDate={selectedDate}
                onSubmit={handleAddEvent}
            />

            <Modal open={showEventDetails} onClose={() => setShowEventDetails(false)}>
                <ModalDialog size="md" sx={{ borderRadius: "4px", boxShadow: "none" }}>
                    <ModalClose />
                    <DialogTitle>Task details</DialogTitle>
                    <DialogContent>
                        {selectedEvent && (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            textTransform: "uppercase",
                                            color: "text.tertiary",
                                            letterSpacing: "0.4px",
                                            mb: 0.5,
                                        }}
                                    >
                                        Task
                                    </Typography>
                                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                                        {selectedEvent.title}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        p: 0.75,
                                        px: 1.5,
                                        border: "1px solid",
                                        borderColor: "rgba(128, 128, 128, 0.2)",
                                        borderRadius: "4px",
                                        bgcolor: "rgba(128, 128, 128, 0.05)",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                bgcolor: getStatusColor(
                                                    selectedEvent.taskStatus || "NOT-STARTED",
                                                ),
                                            }}
                                        />
                                        <Typography level="body-xs" color="neutral">
                                            {getStatusName(
                                                selectedEvent.taskStatus || "NOT-STARTED",
                                            )}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" sx={{ height: 12 }} />
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Clock size={14} opacity={0.6} />
                                        <Typography level="body-xs" color="neutral">
                                            {selectedEvent.allDay
                                                ? "All day"
                                                : selectedEvent.time || "Untimed"}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" sx={{ height: 12 }} />
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <CalendarIcon size={14} opacity={0.6} />
                                        <Typography level="body-xs" color="neutral">
                                            {new Date(
                                                selectedEvent.date + "T00:00:00",
                                            ).toLocaleDateString("en-US", {
                                                weekday: "short",
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                            })}
                                        </Typography>
                                    </Box>
                                </Box>

                                {selectedEvent.description && (
                                    <Box>
                                        <Typography
                                            level="body-sm"
                                            sx={{
                                                fontWeight: 600,
                                                color: "text.primary",
                                                mb: 0.5,
                                            }}
                                        >
                                            Description
                                        </Typography>
                                        <Typography
                                            level="body-sm"
                                            sx={{ color: "text.secondary" }}
                                        >
                                            {selectedEvent.description}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={() => setShowEventDetails(false)}
                            sx={{ boxShadow: "none" }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
